import * as React from 'react';
import {
  DateRangePicker as RACDateRangePicker,
  DateInput,
  DateSegment,
  Button as RACButton,
  Popover,
  Dialog,
  RangeCalendar,
  CalendarGrid as RACCalendarGrid,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  CalendarCell,
  CalendarHeading,
  type DateValue,
} from 'react-aria-components';
import {
  CalendarLine,
  LeftSmallLine,
  RightSmallLine,
} from '@mingcute/react';
import {
  CalendarDate,
} from '@internationalized/date';

import { cn } from '../lib/cn';

/**
 * Klaret DateRangePicker (handoff §10.4).
 * Two-month side-by-side range calendar. Segmented typed entry per locale.
 * range = --tvl-selected band; endpoints = --tvl-accent fill + --tvl-on-accent.
 * Presets column ("Today", "Last 7 days", "This month") as tertiary buttons.
 *
 * Exported names preserved:
 *   DateRangePicker, DateRange
 */

// ---------------------------------------------------------------------------
// DateRange type (preserved from original)
// ---------------------------------------------------------------------------
export interface DateRange {
  start?: Date;
  end?: Date;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function toCalendarDate(d: Date): CalendarDate {
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

function fromCalendarDate(cd: DateValue): Date {
  const c = cd as CalendarDate;
  return new Date(c.year, c.month - 1, c.day);
}

function todayDate(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

// ---------------------------------------------------------------------------
// Two-month range calendar interior (used inside the popover Dialog)
// ---------------------------------------------------------------------------
interface RangeCalendarBodyProps {
  minValue?: DateValue;
  maxValue?: DateValue;
}

function RangeCalendarBody({ minValue, maxValue }: RangeCalendarBodyProps) {
  return (
    <RangeCalendar
      minValue={minValue}
      maxValue={maxValue}
      visibleDuration={{ months: 2 }}
      className="flex gap-6"
    >
      {[0, 1].map((panelIndex) => (
        <div key={panelIndex} className="flex flex-col">
          <div className="mb-1 flex items-center justify-between">
            {panelIndex === 0 ? (
              <RACButton
                slot="previous"
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-[var(--tvl-r2)]',
                  'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] outline-none',
                  'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
                )}
                aria-label="Previous month"
              >
                <LeftSmallLine size={16} aria-hidden />
              </RACButton>
            ) : (
              <span className="h-7 w-7" />
            )}

            <CalendarHeading className="text-[16px] font-semibold leading-[22px] text-[var(--tvl-ink)]" />

            {panelIndex === 1 ? (
              <RACButton
                slot="next"
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-[var(--tvl-r2)]',
                  'text-[var(--tvl-ink-2)] hover:bg-[var(--tvl-wash-hover)] outline-none',
                  'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
                )}
                aria-label="Next month"
              >
                <RightSmallLine size={16} aria-hidden />
              </RACButton>
            ) : (
              <span className="h-7 w-7" />
            )}
          </div>

          <RACCalendarGrid offset={panelIndex === 1 ? { months: 1 } : undefined}>
            <CalendarGridHeader>
              {(day) => (
                <CalendarHeaderCell className="pb-1 text-[11px] font-medium leading-4 text-[var(--tvl-ink-3)]">
                  {day}
                </CalendarHeaderCell>
              )}
            </CalendarGridHeader>
            <CalendarGridBody>
              {(date) => (
                <CalendarCell
                  date={date}
                  className={cn(
                    'flex h-8 w-8 cursor-pointer select-none items-center justify-center',
                    'rounded-[var(--tvl-r2)] text-sm tabular-nums text-[var(--tvl-ink)] outline-none',
                    'hover:bg-[var(--tvl-wash-hover)]',
                    'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
                    // today
                    'data-[today]:ring-1 data-[today]:ring-[var(--tvl-hairline)]',
                    // range interior band
                    'data-[in-range]:rounded-none data-[in-range]:bg-[var(--tvl-selected)]',
                    // range endpoints — accent
                    'data-[selection-start]:rounded-[var(--tvl-r2)] data-[selection-start]:bg-[var(--tvl-accent)] data-[selection-start]:text-[var(--tvl-on-accent)]',
                    'data-[selection-end]:rounded-[var(--tvl-r2)] data-[selection-end]:bg-[var(--tvl-accent)] data-[selection-end]:text-[var(--tvl-on-accent)]',
                    // outside month
                    'data-[outside-month]:opacity-35',
                    // disabled
                    'data-[disabled]:pointer-events-none data-[disabled]:opacity-35',
                    'data-[unavailable]:pointer-events-none data-[unavailable]:opacity-35',
                  )}
                />
              )}
            </CalendarGridBody>
          </RACCalendarGrid>
        </div>
      ))}
    </RangeCalendar>
  );
}

// ---------------------------------------------------------------------------
// DateRangePicker
// Plain interface (not extending RACDateRangePickerProps) to avoid value-type
// conflict between DateRange<Date> and DateRange<CalendarDate>.
// ---------------------------------------------------------------------------
export interface DateRangePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Select range',
      invalid,
      disabled,
      minDate,
      maxDate,
      className,
      id,
      name,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
    },
    ref,
  ) => {
    const racValue =
      value?.start && value?.end
        ? { start: toCalendarDate(value.start), end: toCalendarDate(value.end) }
        : null;

    const racMin = minDate ? toCalendarDate(minDate) : undefined;
    const racMax = maxDate ? toCalendarDate(maxDate) : undefined;

    const today = todayDate();
    const presets: Array<{ label: string; start: Date; end: Date }> = [
      { label: 'Today', start: today, end: today },
      { label: 'Last 7 days', start: addDays(today, -6), end: today },
      { label: 'This month', start: startOfMonth(today), end: endOfMonth(today) },
    ];

    return (
      <RACDateRangePicker
        ref={ref as never}
        id={id}
        value={racValue}
        onChange={(v) => {
          if (!v) {
            onChange?.({ start: undefined, end: undefined });
            return;
          }
          onChange?.({ start: fromCalendarDate(v.start), end: fromCalendarDate(v.end) });
        }}
        isDisabled={disabled}
        aria-invalid={invalid || undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        minValue={racMin}
        maxValue={racMax}
        className={cn('group flex w-full flex-col', className)}
      >
        {/* Trigger */}
        <div
          className={cn(
            'flex h-9 w-full items-center justify-between gap-2 rounded-[var(--tvl-r2)]',
            // Same composite-widget reasoning as DatePicker: a 44px floor on
            // touch so the date segments are tappable.
            '[@media(pointer:coarse)_and_(hover:none)]:min-h-11',
            'border border-[var(--tvl-border-input)] bg-[var(--tvl-raised)] px-3',
            'transition-colors duration-[var(--tvl-d-fast)]',
            'hover:border-[var(--tvl-ink-2)]',
            'group-focus-within:border-[var(--tvl-accent)]',
            'group-focus-within:shadow-[inset_0_0_0_1px_var(--tvl-accent)]',
            invalid && 'border-[var(--tvl-danger)]',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <DateInput slot="start" className="flex h-full items-center gap-0.5">
            {(segment) => (
              <DateSegment
                segment={segment}
                className={cn(
                  'flex h-full items-center',
                  'rounded px-0.5 text-sm tabular-nums text-[var(--tvl-ink)] outline-none',
                  'data-[placeholder]:text-[var(--tvl-ink-3)]',
                  'data-[focused]:bg-[var(--tvl-accent)] data-[focused]:text-[var(--tvl-on-accent)]',
                  'data-[type=literal]:text-[var(--tvl-ink-3)]',
                )}
              />
            )}
          </DateInput>

          <span className="shrink-0 text-sm text-[var(--tvl-ink-3)]">–</span>

          <DateInput slot="end" className="flex h-full flex-1 items-center gap-0.5">
            {(segment) => (
              <DateSegment
                segment={segment}
                className={cn(
                  'flex h-full items-center',
                  'rounded px-0.5 text-sm tabular-nums text-[var(--tvl-ink)] outline-none',
                  'data-[placeholder]:text-[var(--tvl-ink-3)]',
                  'data-[focused]:bg-[var(--tvl-accent)] data-[focused]:text-[var(--tvl-on-accent)]',
                  'data-[type=literal]:text-[var(--tvl-ink-3)]',
                )}
              />
            )}
          </DateInput>

          <RACButton
            className={cn(
              'flex h-6 w-6 shrink-0 items-center justify-center rounded outline-none',
              'text-[var(--tvl-ink-3)] hover:text-[var(--tvl-ink-2)]',
              'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
            )}
          >
            <CalendarLine size={16} aria-hidden />
          </RACButton>
        </div>

        {/* Calendar popover */}
        <Popover
          className={cn(
            'z-[var(--tvl-z-popover)] overflow-hidden rounded-[var(--tvl-r3)]',
            'border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)]',
            'shadow-[var(--tvl-e2)]',
            'data-[entering]:animate-in data-[entering]:fade-in-0 data-[entering]:zoom-in-95',
            'data-[exiting]:animate-out data-[exiting]:fade-out-0 data-[exiting]:zoom-out-95',
          )}
          offset={4}
        >
          <Dialog className="flex gap-0 outline-none">
            {/* Presets sidebar */}
            <div className="flex flex-col gap-1 border-r border-[var(--tvl-hairline)] p-3 pr-4">
              <p className="mb-1 text-[11px] font-medium uppercase tracking-[0.0075em] text-[var(--tvl-ink-3)]">
                Presets
              </p>
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => onChange?.({ start: p.start, end: p.end })}
                  className={cn(
                    'rounded-[var(--tvl-r2)] px-2 py-1 text-left text-sm',
                    'text-[var(--tvl-accent)] underline-offset-4',
                    'hover:bg-[var(--tvl-wash-hover)] hover:underline outline-none',
                    'focus-visible:outline-[2px] focus-visible:outline-solid focus-visible:outline-[color:var(--tvl-focus)] focus-visible:outline-offset-[2px]',
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Two-month calendar */}
            <div className="p-4">
              <RangeCalendarBody minValue={racMin} maxValue={racMax} />
            </div>
          </Dialog>
        </Popover>
      </RACDateRangePicker>
    );
  },
);
DateRangePicker.displayName = 'DateRangePicker';
